import React, { useState, useEffect } from 'react';
import { Users as UsersIcon } from 'lucide-react';
import UserList from '../components/users/UserList';
import UserForm from '../components/users/UserForm';
import UserProfile from '../components/users/UserProfile';
import ActivityLog from '../components/users/ActivityLog';
import { supabase } from '../lib/supabase';
import type { User, UserActivity } from '../types/user';

type ViewMode = 'list' | 'new' | 'edit' | 'profile' | 'activity';

const Users: React.FC = () => {
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setIsLoading(true);
      const { data: profiles } = await supabase
        .from('profiles')
        .select('*')
        .order('created_at', { ascending: false });

      if (profiles) {
        setUsers(profiles.map(profile => ({
          id: profile.id,
          name: profile.name,
          email: profile.email,
          role: profile.role,
          status: profile.status,
          profileId: profile.id,
          mustChangePassword: false,
          createdAt: new Date(profile.created_at),
          updatedAt: new Date(profile.updated_at)
        })));
      }
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateUser = async (data: Omit<User, 'id' | 'createdAt' | 'updatedAt'>) => {
    try {
      // Implement user creation
      await loadUsers();
      setViewMode('list');
    } catch (error) {
      console.error('Error creating user:', error);
    }
  };

  const handleUpdateUser = async (data: User) => {
    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          name: data.name,
          role: data.role,
          status: data.status,
          updated_at: new Date().toISOString()
        })
        .eq('id', data.id);

      if (error) throw error;

      await loadUsers();
      setViewMode('list');
    } catch (error) {
      console.error('Error updating user:', error);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    try {
      const { error } = await supabase
        .from('profiles')
        .delete()
        .eq('id', userId);

      if (error) throw error;

      await loadUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
    }
  };

  const handleBlockUser = async (userId: string) => {
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ status: 'blocked' })
        .eq('id', userId);

      if (error) throw error;

      await loadUsers();
    } catch (error) {
      console.error('Error blocking user:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <UsersIcon className="h-8 w-8 text-blue-600 mr-3" />
            <h1 className="text-2xl font-bold text-gray-900">
              Usuários e Permissões
            </h1>
          </div>
          {viewMode === 'list' && (
            <button
              onClick={() => setViewMode('new')}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Novo Usuário
            </button>
          )}
        </div>

        {viewMode === 'list' && (
          <UserList
            users={users}
            onSelect={(user) => {
              setSelectedUser(user);
              setViewMode('profile');
            }}
            onEdit={(user) => {
              setSelectedUser(user);
              setViewMode('edit');
            }}
            onDelete={handleDeleteUser}
            onBlock={handleBlockUser}
          />
        )}

        {viewMode === 'new' && (
          <UserForm
            onSubmit={handleCreateUser}
            onCancel={() => setViewMode('list')}
          />
        )}

        {viewMode === 'edit' && selectedUser && (
          <UserForm
            initialData={selectedUser}
            onSubmit={handleUpdateUser}
            onCancel={() => setViewMode('list')}
          />
        )}

        {viewMode === 'profile' && selectedUser && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <button
                onClick={() => setViewMode('list')}
                className="text-blue-600 hover:text-blue-800"
              >
                ← Voltar para lista
              </button>
              <button
                onClick={() => setViewMode('activity')}
                className="text-gray-600 hover:text-gray-800"
              >
                Ver Atividades
              </button>
            </div>

            <UserProfile
              user={selectedUser}
              onEdit={() => setViewMode('edit')}
            />
          </div>
        )}

        {viewMode === 'activity' && selectedUser && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <button
                onClick={() => setViewMode('profile')}
                className="text-blue-600 hover:text-blue-800"
              >
                ← Voltar para perfil
              </button>
            </div>

            <ActivityLog activities={[]} />
          </div>
        )}
      </div>
    </div>
  );
};

export default Users;